import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/components/store/CartDrawer.jsx - Shopping Cart Drawer
import React, { useState } from 'react';
import {
    Drawer,
    Box,
    Typography,
    Button,
    IconButton,
    Badge,
    TextField,
    Chip,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Close as CloseIcon,
    ShoppingCart as CartIcon,
    LocalShipping as ShippingIcon,
    LocalOffer as CouponIcon
} from '@mui/icons-material';

import { cartHelpers } from '../../services/ecommerceApi';
import { useCart } from '../../hooks/useCart';
import { useNotification } from '../common/GlobalNotifications';
import CartItemLine from './CartItemLine';
import CartOrderSummary from './CartOrderSummary';

const CartDrawer = ({ open, onClose, onCheckoutClick }) => {
  const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const {
        cart,
        cartTotals,
        loading,
        updateQuantity,
        removeItem,
        applyCoupon,
        removeCoupon
    } = useCart();

    const { showSuccess, showError } = useNotification();
    const [couponCode, setCouponCode] = useState('');
    const [applyingCoupon, setApplyingCoupon] = useState(false);

    const totalSavings = cartHelpers.calculateSavings(cart);

    const handleQuantityChange = async (itemId, newQuantity) => {
        if (newQuantity < 1) {
            await handleRemoveItem(itemId);
            return;
        }

        try {
            await updateQuantity(itemId, newQuantity);
        } catch (error) {
            showError(t('hardcoded.components_store_cartdrawer.error_al_actualizar_cantidad'));
        }
    };

    const handleRemoveItem = async (itemId) => {
        try {
            await removeItem(itemId);
            showSuccess(t('cart.remove'));
        } catch (error) {
            showError(t('hardcoded.components_store_cartdrawer.error_al_eliminar_producto'));
        }
    };

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) return;

        setApplyingCoupon(true);
        try {
            await applyCoupon(couponCode.trim().toUpperCase());
            setCouponCode('');
            showSuccess(t('hardcoded.components_store_cartdrawer.cupon_aplicado_correctamente'));
        } catch (error) {
            showError(error.message || t('hardcoded.components_store_cartdrawer.error_al_aplicar_cupon'));
        } finally {
            setApplyingCoupon(false);
        }
    };

    const handleRemoveCoupon = async () => {
        try {
            await removeCoupon();
            showSuccess(t('hardcoded.components_store_cartdrawer.cupon_removido'));
        } catch (error) {
            showError(t('hardcoded.components_store_cartdrawer.error_al_remover_cupon'));
        }
    };

    const drawerWidth = isMobile ? '100vw' : 400;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: drawerWidth,
                    maxWidth: '100vw',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Badge badgeContent={cartTotals?.itemCount || 0} color="primary">
                            <CartIcon />
                        </Badge>
                        <Typography variant="h6" fontWeight={600}>
                            {t('hardcoded.components_store_cartdrawer.carrito_de_compras')}</Typography>
                    </Box>

                    <IconButton onClick={onClose} edge="end">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Empty Cart */}
            {!loading && (!cart || !cart.items || cart.items.length === 0) && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 4,
                    flex: 1
                }}>
                    <CartIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" textAlign="center" mb={1}>
                        {t('hardcoded.components_store_cartdrawer.tu_carrito_esta_vacio')}</Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
                        {t('hardcoded.components_store_cartdrawer.agrega_algunos_productos_para_empezar')}</Typography>
                    <Button
                        variant="contained"
                        onClick={onClose}
                        sx={{ textTransform: 'none' }}
                    >
                        {i18n.t('hardcoded.components_store_cartdrawer.continuar_comprando')}</Button>
                </Box>
            )}

            {/* Cart Items */}
            {!loading && cart && cart.items && cart.items.length > 0 && (
                <>
                    {/* Items List */}
                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        <Box sx={{ px: 2 }}>
                            {cart.items.map((item) => (
                                <CartItemLine
                                    key={item.id}
                                    item={item}
                                    compact
                                    editable
                                    onDecrease={() => handleQuantityChange(item.id, item.quantity - 1)}
                                    onIncrease={() => handleQuantityChange(item.id, item.quantity + 1)}
                                    onSetQuantity={(qty) => handleQuantityChange(item.id, qty)}
                                    onRemove={() => handleRemoveItem(item.id)}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Coupon Section */}
                    <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                        {cartTotals?.couponCode ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Chip
                                    label={t('hardcoded.components_store_cartdrawer.cupon_couponcode', { couponCode: cartTotals.couponCode })}
                                    color="success"
                                    variant="outlined"
                                    icon={<CouponIcon />}
                                    onDelete={handleRemoveCoupon}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <TextField
                                    size="small"
                                    placeholder={t('hardcoded.components_store_cartdrawer.codigo_de_cupon')}
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    disabled={applyingCoupon}
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={handleApplyCoupon}
                                    disabled={!couponCode.trim() || applyingCoupon}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {applyingCoupon ? t('hardcoded.components_store_cartdrawer.aplicando') : t('hardcoded.components_store_cartdrawer.aplicar')}
                                </Button>
                            </Box>
                        )}

                        {/* Free Shipping Alert */}
                        {cartTotals && !cartTotals.hasFreeShipping && (
                            <Alert
                                severity="info"
                                icon={<ShippingIcon />}
                                sx={{ mb: 2 }}
                            >
                                <Typography variant="body2">
                                    Agrega {cartHelpers.formatCurrency(Math.max(0, 100 - cartTotals.subtotal))} {t('hardcoded.components_store_cartdrawer.mas_para_envio_gratuito')}</Typography>
                            </Alert>
                        )}
                    </Box>

                    {/* Totals */}
                    <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                        <CartOrderSummary cartTotals={cartTotals} totalSavings={totalSavings} showSavings />

                        {/* Checkout Button */}
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={onCheckoutClick}
                            sx={{
                                fontWeight: 600,
                                textTransform: 'none',
                                py: 1.5
                            }}
                        >
                            {t('hardcoded.components_store_cartdrawer.proceder_al_checkout')}</Button>

                        <Button
                            fullWidth
                            variant="text"
                            onClick={onClose}
                            sx={{
                                mt: 1,
                                textTransform: 'none'
                            }}
                        >
                            {i18n.t('hardcoded.components_store_cartdrawer.continuar_comprando')}</Button>
                    </Box>
                </>
            )}
        </Drawer>
    );
};

export default CartDrawer;